import { order } from '@/dataStructures';
import {
	newOrderRequest,
	updateOrderRequest,
	getOrderTypesRequest,
	getOrderByIdRequest,
	openTmsR10Request,
	getCurrentClerkRequest,
	getTripByIdRequest,
	getCustomerByNumberRequest,
	getOrderByQuickSearchRequest,
	getOrderChangelogRequest,
	createParallelSubOrderRequest,
	getEdipartDetailsRequest,
	getEdiLogsRequest,
	updateTripOrdersRequest,
	updateTransporterOrderRequest,
	deleteSubOrderRequest
} from '../requests';
import {
	throwTranslatableError, baseUrl, createDateObject, storageDateFormat, statuses, createSocket, sumValues
} from '@/core';
import { addOrderToTrip, addOrderToTransporter, moveOrderToDifferentTrip, insertSubOrderBefore } from './actions/index';
import { mapOrderSumKeysToFreightLineKey } from '../details/freightLineSumKeys';
import moment from 'moment';
import rdbClient from 'rdb-client';
import deepCopy from 'deepcopy';

function setValue({ commit }, { key, value }) {
	commit('setValue', { key, value });
}

const orderFieldsToCopy = [
	'accepted',
	'etd',
	'eta',
	'chargableWeight',
	'clerk',
	'consignee',
	'consignor',
	'customer',
	'deliveryParty',
	'despatchParty',
	'goodsListFreeText',
	'invoiceFreeText',
	'loadingListFreeText',
	'freightLines',
	'goodsType',
	'grossWeight',
	'internalNotes',
	'loadingMeters',
	'mayGenerateReturnOrder',
	'originalShipper',
	'economicalStatus',
	'status',
	'transporterInstructions',
	'volume',
	'waybillNumber',
	'orderType',
	'incotermsCode',
	'incotermsPlace',
	'documents',
	'totalPrice',
	'currency',
	'invoiceCurrencyPrice',
	'invoiceCurrency',
	'manuallyPriced',
	'priceLines',
];

const getNewEta = (oldEta, oldEtd, newEtd) => {
	if (!oldEta) return { hasTime: false, timeZone: newEtd.timeZone };
	let daysDiff = moment(oldEta.date, storageDateFormat).startOf('day').diff(moment(oldEtd.date, storageDateFormat).startOf('day'), 'days').toString();
	return createDateObject({
		date: moment(newEtd.date, storageDateFormat).add(daysDiff, 'days').format(storageDateFormat),
		hasTime: oldEta.hasTime,
		timeZone: oldEta.timeZone,
	}, false);
};
async function copyOrder({ dispatch, commit, state }, { newEtd, count }) {
	let newEta = getNewEta(state.orderShadowCopy.eta, state.orderShadowCopy.etd, newEtd);
	let orderToCopy = order();

	orderFieldsToCopy.forEach(key => {
		orderToCopy[key] = state.orderShadowCopy[key];
	});
	orderToCopy.etd = newEtd;
	orderToCopy.eta = newEta;
	orderToCopy.status = statuses.order.draft;
	orderToCopy.economicalStatus = statuses.economical.notSettled;

	try {
		let response = null;
		for (let i = 0; i < count; i++) {
			response = await newOrderRequest(orderToCopy);
		}
		if (response)
			dispatch('setEditOrder', response.id);
	} catch (e) {
		if (e.response && e.response.data && e.response.data && e.response.data.errors) {
			commit('setServerErrors', e.response.data.errors);
		}
		throwTranslatableError(e, 'unabledToCopyOrder');
	}
}

async function saveNewOrder({ state, commit }) {
	commit('validate');
	if (state.errors.order.isInvalid || state.errors.subOrders.isInvalid) {
		throw new Error();
	}

	try {
		let newOrder = await newOrderRequest(state.orderShadowCopy);
		commit('setEditOrder', newOrder);
	} catch (e) {
		if (e.response && e.response.data && e.response.data && e.response.data.errors) {
			commit('setServerErrors', e.response.data.errors);
		}
		throwTranslatableError(e, 'failedToSave');
	}
}

async function openTmsR10({}, order) {
	let invalid = [];
	if (!validParty(order.consignor)) {
		invalid.push('consignor');
	}
	if (!validParty(order.consignee)) {
		invalid.push('consignee');
	}
	if (!validParty(order.despatchParty)) {
		invalid.push('despatchParty');
	}
	if (!validParty(order.deliveryParty)) {
		invalid.push('deliveryParty');
	}

	if (!order.orderType || !order.orderType.code) {
		invalid.push('orderType');
	}

	if (!order.etd || !order.etd.date) {
		invalid.push('etd');
	}

	if (!order.clerk || !order.clerk.r10Id) {
		invalid.push('clerk');
	}

	if (invalid.length > 0) {
		let error = new Error('notSyncable');
		error.invalidList = invalid;
		throw error;
	} else {
		await openTmsR10Request(order.id);
	}

	function validParty(party) {
		if (!party) return false;

		return party.name
			&& party.postalCode
			&& party.postalPlace;
	}
}

async function saveOrder({ state, commit, getters, dispatch }) {

	commit('validate');
	if (state.errors.order.isInvalid || state.errors.subOrders.isInvalid) {
		throw new Error();
	}

	// Delete empty additional references
	if (state.orderShadowCopy.additionalReferences) {
		state.orderShadowCopy.additionalReferences = state.orderShadowCopy.additionalReferences.filter(
			reference => reference.value.length
		);
	}

	for (let orderSumKey in mapOrderSumKeysToFreightLineKey) {
		state.orderShadowCopy[orderSumKey] = getters.freightLineSums[orderSumKey];
	}

	let patch = rdbClient.createPatch(state.orderClone, state.orderShadowCopy);

	try {
		let updatedOrder = await updateOrderRequest(state.order.id, { orderUpdates: patch });
		commit('setEditOrder', updatedOrder);
		dispatch('fetchOrderChangelog');
	} catch (e) {
		if (e.response && e.response.data && e.response.data && e.response.data.errors) {
			commit('setServerErrors', e.response.data.errors);
		}
		throwTranslatableError(e, 'failedToSave');
	}
}


async function cleanState({ commit }) {
	let currentClerk = await getCurrentClerkRequest();
	commit('cleanState', currentClerk);
}

async function setEditOrder({ state, commit, dispatch }, id) {
	let oldId = state.order.id;
	try {
		let order = await getOrderByIdRequest(id);
		await dispatch('getOrderTypes');
		commit('setEditOrder', order);
		dispatch('setupSocket', oldId);
		dispatch('fetchEdiName');
		dispatch('fetchEdiLog');
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

async function getOrderTypes({ commit  }) {
	let orderTypes = await getOrderTypesRequest();
	commit('set', (state) => state.orderTypes = orderTypes);
}

async function setEditOrderByQuickSearch({ state, commit, dispatch }, text) {
	let oldId = state.order.id;
	try {
		let order = await getOrderByQuickSearchRequest(text);
		await dispatch('getOrderTypes');
		commit('setEditOrder', order);
		dispatch('setupSocket', oldId);
		dispatch('fetchEdiName');
		dispatch('fetchEdiLog');
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

async function setupSocket({ state, commit, dispatch }, oldId) {
	try {
		let { socket } = state;
		if (!socket) socket = await createSocket();
		if (oldId) socket.emit('unsubscribe', oldId);
		socket.emit('subscribe', state.order.id);
		socket.on('order-updates', (data) => {
			if (data.id !== state.order.id) return;
			dispatch('autoRefreshOrder');
			console.log('update received: ', data);
		});
		commit('setSocket', socket);
	} catch (e) {
		console.log('Failed to setup socket correctly');
		console.error(e);
	}
}

async function autoRefreshOrder({ state, commit, dispatch }) {
	if (!state.order.id) return;
	try {
		let order = await getOrderByIdRequest(state.order.id);
		let existingChanges = rdbClient.createPatch(state.orderClone, state.orderShadowCopy);
		// changed state prevents refresh
		if (existingChanges.length > 0) {
			let paths = new Set(existingChanges.map(x => x.path));
			let newData = rdbClient.createPatch(state.orderShadowCopy, order);
			newData = newData.filter(x => !paths.has(x.path) );
			commit('autoRefreshOrder', newData);
		} else {
			commit('setEditOrder', order);
		}
		dispatch('fetchOrderChangelog');
		dispatch('fetchEdiName');
		dispatch('fetchEdiLog');
	} catch (e) {
		throwTranslatableError(e, 'failedToFetch');
	}
}

function getWaybill(_, orderId) {
	let base = baseUrl();
	let url = `${base}/documents/wayBill/${orderId}`;
	window.open(url);
}

function getPackageLabel(_, orderId) {
	let base = baseUrl();
	let locale = localStorage.getItem('locale');

	let url = `${base}/documents/packageLabel/${locale}/${orderId}`;
	window.open(url);
}

async function getAndSetCustomerByNumber({ commit }, customerNumber) {
	try {
		let customer = await getCustomerByNumberRequest(customerNumber);
		commit('setCustomer', { customer });
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

function setSelectedSubOrder({ commit, dispatch }, subOrderId) {
	commit('setSelectedSubOrder', subOrderId);
	dispatch('setSelectedSubOrderTrip');
}

async function setSelectedSubOrderTrip({ state, commit }) {
	let trip;
	if (state.selectedSubOrder && state.selectedSubOrder.tripId) {
		trip = state.trips.find(x => x.id === state.selectedSubOrder.tripId);
		if (!trip) {
			commit('setIsLoadingTrip', true);
			trip = await getTripByIdRequest(state.selectedSubOrder.tripId);
			commit('addTrip', trip);
			commit('setIsLoadingTrip', false);
		}
	}

	commit('setSelectedSubOrderTrip', trip);
}

async function fetchOrderChangelog({ commit, state }) {
	try {
		let changelog = await getOrderChangelogRequest(state.order.id);
		commit('setOrderChangelog', changelog);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}


async function fetchEdiLog({ commit, state }) {
	try {
		let ediLogs = await getEdiLogsRequest(state.order.id);
		commit('setEdiLogs', ediLogs);
	} catch (e) {
		throwTranslatableError(e, 'failedToFetchEdiLogs');
	}
}

async function createParallelSubOrder({}, subOrderId) {
	try {
		await createParallelSubOrderRequest(subOrderId);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToCreate');
	}
}

async function fetchEdiName({ state, commit }) {
	try {
		if (state.orderClone.edi && state.orderClone.edi.in_edipartId) {
			let edi = await getEdipartDetailsRequest(state.orderClone.edi.in_edipartId);
			commit('set', () => { state.ediName = edi.name; });
		}
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

async function removeOrderFromTrip({ dispatch }, { order, merge }) {
	if (!order) return;
	let trip = await getTripByIdRequest(order.tripId);

	let subOrderUpdates = [{
		removedOrderId: order.id,
		merge
	}];

	// Format trip to remove data not needed server-side and take clone
	let tripClone = deepCopy(trip);

	// Remove orders
	let cleanOrderList = trip.orders.filter(x => x.id !== order.id);
	trip.volume = sumValues(cleanOrderList, 'volume');
	trip.grossWeight = sumValues(cleanOrderList, 'grossWeight');
	trip.loadingMeters = sumValues(cleanOrderList, 'loadingMeters');

	try {
		let patch = rdbClient.createPatch(tripClone, trip);
		await updateTripOrdersRequest(trip.id, { tripPatches: patch, subOrderUpdates });
		dispatch('autoRefreshOrder');
	} catch (err) {
		throw new Error('failedToRemoveOrderTrip');
	}
}

async function removeOrderFromTransporter({ dispatch }, { order, merge }) {
	if (!order) return;

	try {
		let removedIds = [{ orderId: order.orderId, id: order.id }];
		await updateTransporterOrderRequest(order.transporterOrderId, { removedOrders: removedIds, merge });
		dispatch('autoRefreshOrder');
	} catch (err) {
		throw new Error('failedToRemoveOrderTransporter');
	}
}

async function getSubOrderTrip({ state, commit }, tripId) {
	let trip = state.trips.find(x => x.id === tripId);
	if (!trip) {
		trip = await getTripByIdRequest(tripId);
		commit('addTrip', trip);
	}
	return trip;
}

async function addSubOrderBefore() {
	
}

async function removeSubOrder({ state, commit, dispatch }, id) {
	try {
		await deleteSubOrderRequest(id);
		commit('setCurrentOrderTabIndex', state.mainOrderTabIndex);
		dispatch('autoRefreshOrder');
	} catch (err) {
		throw new Error('failedToDeleteSubOrder');
	}
}

function setDeviationBindings({ commit }, deviationBindings) {
	commit('setDeviationBindings', deviationBindings);
}

export default {
	addOrderToTrip,
	addOrderToTransporter,
	moveOrderToDifferentTrip,
	insertSubOrderBefore,
	setValue,
	saveNewOrder,
	saveOrder,
	cleanState,
	setEditOrder,
	setEditOrderByQuickSearch,
	setupSocket,
	autoRefreshOrder,
	setSelectedSubOrder,
	setSelectedSubOrderTrip,
	openTmsR10,
	getWaybill,
	getPackageLabel,
	getAndSetCustomerByNumber,
	copyOrder,
	fetchOrderChangelog,
	createParallelSubOrder,
	getOrderTypes,
	fetchEdiName,
	fetchEdiLog,
	removeOrderFromTrip,
	removeOrderFromTransporter,
	getSubOrderTrip,
	addSubOrderBefore,
	removeSubOrder,
	setDeviationBindings,
};
